import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AssinaturaBannerNivel = 'success' | 'warning' | 'danger' | 'info';

export interface AssinaturaBannerState {
  status: string;
  titulo: string;
  mensagem: string;
  sugestao?: string;
  nivel: AssinaturaBannerNivel;
  acaoLabel?: string;
  acaoUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AssinaturaBannerService {
  private readonly _banner$ = new BehaviorSubject<AssinaturaBannerState | null>(null);
  readonly banner$ = this._banner$.asObservable();

  syncFromBlacklist(result: any): void {
    const assinaturaEncontrada = !!result?.assinaturaEncontrada;

    if (!assinaturaEncontrada) {
      this.clear();
      return;
    }

    const status = this.resolveStatusFromBlacklist(result);
    const mensagemBackend = (result?.assinaturaMensagem || '').toString().trim();
    const initPoint = (result?.empresaAssinatura?.initPoint || result?.initPoint || '').toString().trim();

    this.showFromStatus(status, mensagemBackend, initPoint);
  }

  private resolveStatusFromBlacklist(result: any): string {
    const statusDireto = (result?.assinaturaStatus || '').toString().toLowerCase().trim();
    if (statusDireto) {
      return statusDireto;
    }

    const statusReferencia = (result?.empresaAssinatura?.status || '').toString().toLowerCase().trim();
    if (statusReferencia) {
      console.log('[AssinaturaBanner] status via fallback empresaAssinatura.status', {
        status: statusReferencia,
        codigo: result?.codigo,
      });
      return statusReferencia;
    }

    const mensagem = (result?.assinaturaMensagem || '').toString().toLowerCase();
    if (mensagem.includes('pendente')) {
      console.log('[AssinaturaBanner] status inferido via assinaturaMensagem', {
        status: 'pending',
        codigo: result?.codigo,
        mensagem: result?.assinaturaMensagem,
      });
      return 'pending';
    }
    if (mensagem.includes('pausad')) {
      console.log('[AssinaturaBanner] status inferido via assinaturaMensagem', {
        status: 'paused',
        codigo: result?.codigo,
        mensagem: result?.assinaturaMensagem,
      });
      return 'paused';
    }
    if (mensagem.includes('cancelad')) {
      console.log('[AssinaturaBanner] status inferido via assinaturaMensagem', {
        status: 'cancelled',
        codigo: result?.codigo,
        mensagem: result?.assinaturaMensagem,
      });
      return 'cancelled';
    }
    if (mensagem.includes('autorizad') || mensagem.includes('ativa')) {
      console.log('[AssinaturaBanner] status inferido via assinaturaMensagem', {
        status: 'authorized',
        codigo: result?.codigo,
        mensagem: result?.assinaturaMensagem,
      });
      return 'authorized';
    }

    return '';
  }

  showFromStatus(status: string, mensagemBackend?: string, initPoint?: string): void {
    const statusNormalizado = (status || '').toLowerCase();

    if (statusNormalizado === 'authorized') {
      this.clear();
      return;
    }

    const state: AssinaturaBannerState = {
      status: statusNormalizado || 'desconhecido',
      titulo: 'Status da assinatura',
      mensagem: mensagemBackend || 'Foi identificado um status de assinatura que requer atenção.',
      nivel: 'info',
      acaoLabel: undefined,
      acaoUrl: undefined,
    };

    if (statusNormalizado === 'pending') {
      state.nivel = 'warning';
      state.titulo = 'Assinatura pendente';
      state.mensagem = mensagemBackend || 'A assinatura está pendente de confirmação.';
      state.sugestao = 'Sugestão: conclua a confirmação/pagamento para evitar bloqueio.';
    } else if (statusNormalizado === 'paused') {
      state.nivel = 'danger';
      state.titulo = 'Assinatura pausada';
      state.mensagem = mensagemBackend || 'A assinatura está pausada.';
      state.sugestao = 'Sugestão: verifique a cobrança e entre em contato com o suporte.';
    } else if (statusNormalizado === 'cancelled') {
      state.nivel = 'danger';
      state.titulo = 'Assinatura cancelada';
      state.mensagem = mensagemBackend || 'A assinatura foi cancelada.';
      state.sugestao = 'Sugestão: reative a assinatura com o suporte para normalizar o acesso.';
    }

    if (initPoint && (statusNormalizado === 'pending' || statusNormalizado === 'paused' || statusNormalizado === 'cancelled')) {
      state.acaoLabel = 'Pagamento';
      state.acaoUrl = initPoint;
    }

    this._banner$.next(state);
  }

  clear(): void {
    this._banner$.next(null);
  }
}
